// src/Pages/AiChat.js
import React, { useState, useRef, useEffect } from 'react';
import { ArrowUp, ArrowDown, CornerRightUp, CornerRightDown } from 'react-feather';
import './AiChat.css';

import NodeBackend from "../../Resources/NodeBackend";
const backend = new NodeBackend();
const API_ENDPOINT = backend.getPath();

function getTodayKeyToronto() {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);

  const year  = parts.find(p => p.type === 'year').value;
  const month = parts.find(p => p.type === 'month').value;
  const day   = parts.find(p => p.type === 'day').value;

  return `${year}-${month}-${day}`; // YYYY-MM-DD
}

const PUBLIC_CHAT_CREDITS_KEY = 'ns_public_ai_credits';
const PUBLIC_CHAT_DAILY_LIMIT = 15;

export default function AiPublicChat({ setPage }) {
    const [messages, setMessages] = useState([
        {
            role: 'assistant',
            content:
                "Hello, I'm Next Stock AI. Ask me anything about stocks, sectors, market trends, or recent financial news.",
        },
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const chatEndRef = useRef(null);
    const [showSuggestions, setShowSuggestions] = useState(true);
    const [aiHasResponded, setAiHasResponded] = useState(false);

    const [accessChecked, setAccessChecked] = useState(false);
    const [accessAllowed, setAccessAllowed] = useState(false);

    function getTodayCredits() {
      try {
        const stored = localStorage.getItem(PUBLIC_CHAT_CREDITS_KEY);
        const todayKey = getTodayKeyToronto();

        if (!stored) {
          return { date: todayKey, count: 0 };
        }

        const parsed = JSON.parse(stored);
        if (parsed.date !== todayKey) {
          return { date: todayKey, count: 0 };
        }

        return { date: parsed.date, count: parsed.count || 0 };
      } catch {
        return { date: getTodayKeyToronto(), count: 0 };
      }
    }

    function saveTodayCredits(count) {
      const todayKey = getTodayKeyToronto();
      localStorage.setItem(
        PUBLIC_CHAT_CREDITS_KEY,
        JSON.stringify({ date: todayKey, count })
      );
    }


    useEffect(() => {
        async function validateAccess() {
            try {
                const params = new URLSearchParams(window.location.search);
                const accessParam = params.get('access') || '';

                if (!accessParam) {
                    window.location.href = 'https://app.nextstock.ca/';
                    return;
                }

                const clientDate = getTodayKeyToronto();

                const resValidate = await fetch(API_ENDPOINT + 'validate-ai-public-access', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        access: accessParam,
                        clientDate,
                    }),
                });

                const result = await resValidate.json();

                if (!result.success || result.status !== 'access-granted') {
                    window.location.href = 'https://app.nextstock.ca/';
                    return;
                }

                console.log(result.status);
                setAccessAllowed(true);
            } catch (e) {
                window.location.href = 'https://app.nextstock.ca/';
            } finally {
                setAccessChecked(true);
            }
        }

        validateAccess();
    }, []);



useEffect(() => {
    if (messages.length === 0) return;

    const chatContainer = chatEndRef.current?.parentElement;
    const lastMessage = messages[messages.length - 1];
    if (!chatContainer || !lastMessage) return;

    requestAnimationFrame(() => {
        if (window.matchMedia('(max-width: 760px)').matches) {
            chatContainer.scrollTo({ top: chatContainer.scrollHeight, behavior: 'auto' });
            return;
        }
        if (lastMessage.role === 'user') {
            const bubbles = chatContainer.querySelectorAll('.ai-chat-bubble-user');
            const lastUserBubble = bubbles[bubbles.length - 1];
            if (lastUserBubble) {
                const bubbleRect = lastUserBubble.getBoundingClientRect();
                const containerRect = chatContainer.getBoundingClientRect();

                const scrollPosition =
                    chatContainer.scrollTop +
                    (bubbleRect.top - containerRect.top) -
                    40; // pequeno offset

                chatContainer.scrollTo({
                    top: scrollPosition,
                    behavior: 'smooth',
                });
            }
        } 
        else if (lastMessage.role === 'assistant' && !loading) {
            const distanceFromBottom =
                chatContainer.scrollHeight -
                chatContainer.scrollTop -
                chatContainer.clientHeight;

            if (distanceFromBottom < 300) {
                chatContainer.scrollTo({
                    top: chatContainer.scrollHeight - 20,
                    behavior: 'smooth',
                });
            }
        }
    });
}, [messages, loading]);
    
    async function handleSend(customMessage) {
        // 🧹 Ensure input is a clean string
        let rawMessage = typeof customMessage === 'string' ? customMessage : input;
        if (typeof rawMessage !== 'string') rawMessage = ''; // fallback

        // 🧽 Normalize whitespace and remove funky characters
        const trimmed = rawMessage
            .replace(/\r?\n\s*\r?\n/g, '\n') // remove excessive blank lines
            .replace(/target=\s*_blank/gi, "")
            .replace(/rel=\s*noopener\s*noreferrer/gi, "")
            .replace(/>\s*watch/gi, "> watch")
            .trim();

        if (!trimmed || loading) return;

        // 🔒 Limite diário de 15 mensagens por navegador
        const currentCredits = getTodayCredits();
        if (currentCredits.count >= PUBLIC_CHAT_DAILY_LIMIT) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content:
                "You have reached today's limit of 15 questions in the public Next Stock AI chat. " +
                "Please come back tomorrow or log in to the app for full access.",
            },
          ]);
          return;
        }

        const newCount = currentCredits.count + 1;
        saveTodayCredits(newCount);

        const userMessage = { role: 'user', content: trimmed };

        const newMessages = [...messages, userMessage];

        setMessages(newMessages);
        setInput('');
        setLoading(true);

        try {
            const res = await fetch(API_ENDPOINT + 'ai-chat-with-web', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    behaviorId: 'sales',
                    messages: newMessages.map((m) => ({
                        role: m.role,
                        content: m.content,
                    })),
                }),
            });

            const data = await res.json();

            if (!data.success) {
                console.error('AI error:', data.error);
                setMessages((prev) => [
                    ...prev,
                    {
                        role: 'assistant',
                        content:
                            "Sorry, I had trouble retrieving the information. Please try again in a few seconds.",
                    },
                ]);
                setAiHasResponded(true);
            } else {
                setMessages((prev) => [
                    ...prev,
                    { role: 'assistant', content: data.reply },
                ]);
            }
        } catch (err) {
            console.error(err);
            setMessages((prev) => [
                ...prev,
                {
                    role: 'assistant',
                    content:
                        "Sorry, there was a connection error. Please check your internet or try again shortly.",
                },
            ]);
        } finally {
            setLoading(false);
            setShowSuggestions(false);
        }
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }


function renderMarkdown(text = "") {
  if (!text) return { __html: "" };

  let html = text
    // **bold**
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")

    // markdown [text](url)
    .replace(
      /\[(.*?)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    )

    // plain links (http + https + www)
    .replace(
      /\b((https?:\/\/|www\.)[^\s<]+)\b/gi,
      (match) => {
        let url = match;
        if (url.startsWith("www.")) url = "https://" + url;
        return `<a href="${url}" target="_blank" rel="noopener noreferrer">${match}</a>`;
      }
    )

    // newline → <br>
    .replace(/\n/g, "<br />");

  return { __html: html };
}

  if (!accessChecked || !accessAllowed) {
    return null; // or a small "Loading..." if you prefer
  }

return (
  <div className="ai-chat-page">

    <div className="ai-chat-main">
      {/* <h2 className="ai-chat-title">Next Stock AI Chat</h2> */}

      <div className={`ai-chat-window ${aiHasResponded ? 'expand-smooth' : ''}`}>
        {/* === Chat Messages === */}
        <div className="ai-chat-messages">
          {messages.map((msg, index) => (
            <div
              key={index}
              className={
                'ai-chat-bubble ' +
                (msg.role === 'user'
                  ? 'ai-chat-bubble-user'
                  : 'ai-chat-bubble-assistant')
              }
            >
              {msg.role === 'assistant' ? (
                <div
                  className="ai-chat-markdown"
                  dangerouslySetInnerHTML={renderMarkdown(
                    msg.content
                  )}
                />
              ) : (
                msg.content
              )}
            </div>
          ))}

          {loading && (
            <div className="ai-chat-bubble ai-chat-bubble-assistant">
              Thinking...
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* === Input Area === */}
        <div className="ai-new-input-area">
          <div className="ai-input-inner">
            <textarea
              className="ai-input-field"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask anything"
              rows={1}
            />

            <button
              className="ai-input-send"
              onClick={handleSend}
              disabled={loading}
            >
              <ArrowUp size={18}/>
            </button>
          </div>
        </div>

        {/* === Conversation Starters === */}
        <div className={`starter-section${showSuggestions ? "" : " starter-collapsed"}`}>
          {/* 🔽 Toggle button */}
          <button
            className="starter-toggle-btn"
            onClick={() => setShowSuggestions(!showSuggestions)}
          >
            {showSuggestions ? <CornerRightDown size={16}/> : <CornerRightUp size={16}/>}
          </button>

          {showSuggestions && (
            <>
              <div className="starter-carousel-wrapper">
                <div className="starter-carousel">
                  {[
                    "❓ What is Next Stock?",
                    "🛡 What is Today's Next Stock's Long-Term Pick?",
                    "⚡ What is Today's Next Stock's Short-Term Pick?",
                    "🔟 How is Next Stock's 10K USD portfolio looking?",
                    "🎥 What was Next Stock's latest video highlight?",
                    "📊 What were Next Stock's Top 3 Value Stocks (US & Canada)?",
                    "🚀 What were Next Stock's Top 3 Dividend Stocks (US & Canada)?",
                    "🤖 What were Next Stock's Top 3 Growth Stocks (US & Canada)?",
                    "🔥 Show me Next Stock's Top 3 Attractive Stocks (USA)",
                    "🔥 Show me Next Stock's Top 3 Attractive Stocks (Canada)",
                    "📈 What were Next Stock's Top 3 Pricey Stocks (US & Canada)?",
                    "🧐 What is the current and historical accuracy of Next Stock?",
                  ].map((text, i) => (
                    <button
                      key={i}
                      className="starter-btn"
                      onClick={() => handleSend(text)}
                    >
                      {text}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  </div>
);


}
